import React from 'react';
import { useNotificaciones } from 'contexts/NotificacionContext';
import { CategoriaNotificacion } from 'types/notificacion.types';

// ─────────────────────────────────────────────────────────────────
// NotificacionToast
// Snackbar flotante para notificaciones en tiempo real (SSE).
// COMUNICADO: barra y acento azul oscuro, dura 10 s.
// ALERTA: barra roja, dura 5 s.
// RECORDATORIO: barra ámbar, dura 5 s.
// ─────────────────────────────────────────────────────────────────

const CATEGORIA_STYLE: Record<CategoriaNotificacion, {
  bar: string; iconBg: string; iconColor: string; label: string; labelCls: string;
}> = {
  COMUNICADO:   { bar: 'bg-blue-700',   iconBg: 'bg-blue-100',  iconColor: 'text-blue-700',  label: 'Comunicado',   labelCls: 'text-blue-700' },
  ALERTA:       { bar: 'bg-red-500',    iconBg: 'bg-red-100',   iconColor: 'text-red-600',   label: 'Alerta',       labelCls: 'text-red-600' },
  RECORDATORIO: { bar: 'bg-amber-500',  iconBg: 'bg-amber-100', iconColor: 'text-amber-700', label: 'Recordatorio', labelCls: 'text-amber-700' },
};

const D_BELL  = 'M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0';
const D_WARN  = 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z';
const D_CLOCK = 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z';

const ICON_PATH: Record<CategoriaNotificacion, string> = {
  COMUNICADO:   D_BELL,
  ALERTA:       D_WARN,
  RECORDATORIO: D_CLOCK,
};

const NotificacionToast: React.FC = () => {
  const { toast, descartarToast } = useNotificaciones();

  if (!toast) return null;

  const style = CATEGORIA_STYLE[toast.categoria] ?? CATEGORIA_STYLE.COMUNICADO;
  const icon  = ICON_PATH[toast.categoria]      ?? D_BELL;

  return (
    <div
      className="fixed bottom-5 right-5 z-[100] w-80 bg-white rounded-2xl
                 shadow-2xl border border-slate-200 overflow-hidden
                 animate-slide-up"
      role="alert"
    >
      {/* Barra de progreso coloreada según categoría */}
      <div className={`h-0.5 ${style.bar} animate-shrink-bar`}/>

      <div className="p-4 flex items-start gap-3">
        {/* Ícono */}
        <div className={`w-8 h-8 rounded-xl ${style.iconBg} flex items-center justify-center flex-shrink-0`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth={1.5} className={`w-4 h-4 ${style.iconColor}`}>
            <path strokeLinecap="round" strokeLinejoin="round" d={icon}/>
          </svg>
        </div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold uppercase tracking-wide mb-0.5 ${style.labelCls}`}>
            {style.label}
          </p>
          <p className="text-sm font-semibold text-slate-800 leading-snug">{toast.titulo}</p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2"
            dangerouslySetInnerHTML={{ __html: toast.mensaje.replace(/<[^>]*>/g, ' ').trim() }}/>
          {toast.requiereConfirmacion && !toast.confirmada && (
            <p className="text-[10px] text-amber-600 font-semibold mt-1">
              Requiere confirmación de lectura
            </p>
          )}
        </div>

        {/* Cerrar */}
        <button
          onClick={descartarToast}
          className="p-1 rounded-lg text-slate-400 hover:text-slate-600
                     hover:bg-slate-100 transition-colors flex-shrink-0"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NotificacionToast;
